import { useContext, useEffect, useState } from 'react';
import { Controller } from 'react-hook-form';

// MUI Import
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import ExpandMoreRounded from '@material-ui/icons/ExpandMoreRounded';
import FormHelperText from '@material-ui/core/FormHelperText';
import styles from './styles.module.css';
import { DealerDetailsContext } from '@/context/dealerDetailsContext';

interface Option {
  id: number;
  name: string;
}

interface ComponentProps {
  options: Option[];
  name: string;
  title: string;
  placeholder?: string;
}

const AutoCompleteWithAddition = ({ options, name, title, placeholder }: ComponentProps) => {
  const {
    trigger,
    setValue,
    control,
    getValues,
    formState: { errors },
  } = useContext(DealerDetailsContext);

  const [inputValue, setInputValue] = useState('');
  const [open, setOpen] = useState(false);
  const [createOption, setCreateOption] = useState<Option | null>(null);

  useEffect(() => {
    const currentValue = getValues(name);
    if (currentValue && typeof currentValue === 'object' && currentValue.name) {
      setInputValue(currentValue.name);
    } else {
      setInputValue('');
    }
  }, [getValues, name]);

  const validate = () => trigger(name);

  const handleChange = (event: React.ChangeEvent<Element>, selectedOption: Option | null) => {
    if (selectedOption) {
      setValue(name, selectedOption.id === -1 ? { id: -1, name: inputValue } : selectedOption);
      setValue('company_name',selectedOption.id === -1?inputValue:selectedOption?.name)
      setInputValue(selectedOption.id === -1 ? inputValue : selectedOption.name); 
    } else {
      setValue('company_name','') 
      setValue(name, { id: -1, name: '' });
      setInputValue(''); 
    }
    trigger('company')
    setCreateOption(null); // Reset create option
    setTimeout(()=>{
      trigger()
    },100)
  };
 
  const handleInputChange = (event: React.ChangeEvent<Element>, newInputValue: string) => {
    setInputValue(newInputValue); 
    const existingOption = options.find(option => option.name.toLowerCase() === newInputValue.toLowerCase().trim());
    if (!existingOption && newInputValue.trim() !== '') {
      setCreateOption({ id: -1, name: `Create "${newInputValue}"` });
    } else {
      setCreateOption(null);
    }
    //Force dropdown to stay open if value is not empty 
    if (newInputValue.trim() !== '') {
      setOpen(true);
    }
  };

  const handleBlur = () => {
    validate();
  };

  const filteredOptions = options.filter(option =>
    option.name.toLowerCase().includes(inputValue.toLowerCase().trim())
  );

  if (createOption) {
    filteredOptions.push(createOption);
  }

  return (
    <>
      <Controller 
        name={name}
        control={control}
        rules={{ 
          // required: { value: true, message: `${title} is required` 
          validate: (value) => {
            const validName = /^[a-zà-ÿA-ZÀ-ÿ](?!.*[ ']{2})[ a-zA-Z']+$/;
            if (!value || value.id === -1 && value.name.trim() === '') {
              return `${title} is required`;
            }
            if (!validName.test(value.name)) {
              return 'Invalid name format';
            }
            return true;
          }
          }}
        render={({ field }) => (
          <Autocomplete
            classes={{
              inputRoot: styles.autocomplete,
              input: styles.autocompleteInput,
            }}
            popupIcon={<ExpandMoreRounded />}
            options={filteredOptions}
            filterOptions={(x) => x} //Disable default material-ui filtering
            onChange={handleChange} 
            onBlur={handleBlur}
            getOptionLabel={(opt: Option) => opt.name || ''}
            value={field.value || { id: -1, name: inputValue }}
            inputValue={inputValue}
            renderInput={(params) => (
              <TextField
                {...params}
                variant="outlined"
                placeholder={placeholder}
                error={Boolean(errors[name])} 
                value={inputValue}
                onClick={() => setOpen(true)}
                onChange={(e) => handleInputChange(e, e.target.value)}
              />
            )}
            open={open}
            onClose={() => setOpen(false)}
          />
        )}
      />
      {errors[name] && (
        <FormHelperText error={true} id={`${name}-error`} className="input-helper-text">
          {errors[name].message}
        </FormHelperText>
      )}
    </>
  );
};

export default AutoCompleteWithAddition;
